import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '../lp2/ui/button.tsx';
import { CreditCard, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const PaymentForm = ({ clientSecret, currency = 'CHF' }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Get currency symbol and name
    const getCurrencyInfo = (currencyCode) => {
        switch (currencyCode) {
            case 'EUR':
                return { symbol: '€', name: 'Euro' };
            case 'CHF':
            default:
                return { symbol: 'CHF', name: 'Schweizer Franken' };
        }
    };

    const currencyInfo = getCurrencyInfo(currency);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setErrorMessage(null);

        if (!stripe || !elements) {
            setLoading(false);
            return;
        }

        try {
            const { error } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/profile`,
                },
                redirect: 'if_required',
            });

            if (error) {
                setErrorMessage(error.message);
                setLoading(false);
                return;
            }

            // Den Zahlungsstatus serverseitig bei Stripe verifizieren lassen.
            // Der Server glaubt dem Browser nicht, sondern fragt Stripe selbst –
            // deshalb ist dieser Aufruf nicht faelschbar.
            try {
                await api.post('/confirm-subscription');
            } catch (confirmError) {
                // Die Zahlung ist durch; nur die Statusaktualisierung hat
                // gehakt. Der Stripe-Webhook zieht das nach.
                console.warn('Statusabgleich fehlgeschlagen:', confirmError);
            }

            localStorage.setItem('paymentSuccess', 'Ihre Zahlung war erfolgreich!');
            navigate('/profile');
        } catch (error) {
            setErrorMessage(error.message);
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Payment Element */}
            <div className="space-y-4">
                <div className="flex items-center mb-4">
                    <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">
                        Zahlungsinformationen
                    </h3>
                </div>
                
                {/* Currency Information */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-800">Währung:</span>
                        <span className="text-sm text-blue-900 font-semibold">
                            {currencyInfo.symbol} ({currencyInfo.name})
                        </span>
                    </div>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <PaymentElement />
                </div>
            </div>

            {/* Error Message */}
            {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                        <h3 className="text-sm font-semibold text-red-800 mb-1">
                            Zahlungsfehler
                        </h3>
                        <p className="text-sm text-red-700">
                            {errorMessage}
                        </p>
                    </div>
                </div>
            )}

            {/* Submit Button */}
            <Button
                type="submit"
                disabled={loading || !stripe}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
                {loading ? (
                    <div className="flex items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Zahlung wird verarbeitet...
                    </div>
                ) : (
                    <div className="flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Jetzt registrieren
                    </div>
                )}
            </Button>

            {/* Additional Info */}
            <div className="text-center">
                <p className="text-sm text-gray-500">
                    Nach erfolgreicher Zahlung werden Sie automatisch zu Ihrem Profil weitergeleitet.
                </p>
            </div>
        </form>
    );
};

export default PaymentForm;